import { VisibilityOutlined } from "@mui/icons-material";
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";

type Props = {
    amounts: string[];
    components: string[];
    adjs: string[];
    collectionIds: string[];
    acsCollection?: number;
    acsAdjustment?: number;
    surchargePaid?: number;
    surchargeAdjustment?: number;
};

const labels: { [key: string]: string } = {
    lineRent: "Line Rent",
    unitsAmount: "Units Amount",
    connectionCharge: "Connection Charges",
};

const CollectionDetails = ({
    amounts,
    components,
    adjs,
    collectionIds,
    acsCollection,
    acsAdjustment,
    surchargePaid,
    surchargeAdjustment,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [details, setDetails] = useState<Record<string, any>>({});

    const calculateDetails = (
        amounts: string[],
        adjs: string[],
        components: string[],
        collectionIds: string[]
    ) => {
        const newDetails = new Map();

        for (const [i, comp] of Object.entries(components)) {
            const idx = parseInt(i);

            if (newDetails.has(comp)) {
                const existing = newDetails.get(comp);
                newDetails.set(comp, {
                    amount: existing.amount + parseFloat(amounts[idx]),
                    adj: existing.adj + parseFloat(adjs[idx]),
                    collectionIds: [
                        ...existing.collectionIds,
                        collectionIds[idx],
                    ],
                });
            } else {
                newDetails.set(comp, {
                    amount: parseFloat(amounts[idx]),
                    adj: parseFloat(adjs[idx]),
                    collectionIds: [collectionIds[idx]],
                });
            }
        }

        setDetails(Object.fromEntries(newDetails));
    };

    useEffect(() => {
        calculateDetails(amounts, adjs, components, collectionIds);
    }, [amounts, adjs, components, collectionIds]);

    let totalAmount = 0;
    let totalAdjustment = 0;

    Object.values(details).forEach((v) => {
        totalAmount += v.amount ?? 0;
        totalAdjustment += v.adj ?? 0;
    });

    totalAmount += acsCollection ?? 0;
    totalAmount += surchargePaid ?? 0;

    totalAdjustment += acsAdjustment ?? 0;
    totalAdjustment += surchargeAdjustment ?? 0;

    const grandTotal = totalAmount + totalAdjustment;

    return (
        <Box>
            <IconButton size="small" onClick={() => setOpen(true)}>
                <VisibilityOutlined fontSize="small" />
            </IconButton>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
                <DialogContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Component</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Adjustment</TableCell>
                                <TableCell>Collection IDs</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {Object.entries(details).map(([k, v]: any[]) => (
                                <TableRow key={k}>
                                    <TableCell>{labels[k] || k}</TableCell>
                                    <TableCell>{v.amount ?? 0}</TableCell>
                                    <TableCell>{v.adj ?? 0}</TableCell>
                                    <TableCell>
                                        {v.collectionIds.join(", ")}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {(acsCollection !== undefined || acsAdjustment !== undefined) && (acsCollection || acsAdjustment) ? (
                                <TableRow key="acs">
                                    <TableCell>Additional Charges</TableCell>
                                    <TableCell>{acsCollection ?? 0}</TableCell>
                                    <TableCell>{acsAdjustment ?? 0}</TableCell>
                                    <TableCell>-</TableCell>
                                </TableRow>
                            ) : null}
                            {(surchargePaid !== undefined || surchargeAdjustment !== undefined) && (surchargePaid || surchargeAdjustment) ? (
                                <TableRow key="surcharge">
                                    <TableCell>Surcharge</TableCell>
                                    <TableCell>{surchargePaid ?? 0}</TableCell>
                                    <TableCell>{surchargeAdjustment ?? 0}</TableCell>
                                    <TableCell>-</TableCell>
                                </TableRow>
                            ) : null}
                            <TableRow key="total" sx={{ '& > *': { fontWeight: 'bold' } }}>
                                <TableCell>Total</TableCell>
                                <TableCell>{totalAmount}</TableCell>
                                <TableCell>{totalAdjustment}</TableCell>
                                <TableCell>Grand Total: {grandTotal}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default CollectionDetails;
